import {
  JobProgressionFlow as JobProgressionFlowProto,
  JobProgressionStepStatus as JobProgressionStepStatusProto,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { Model } from 'infra/model'
import { once } from 'lodash'
import { makeObservable, observable } from 'mobx'
import { Id } from 'types/definitions'
import { maybeParseDate } from 'utils/date-util'
import { timestampToISO } from 'utils/grpc-util'
import { match } from 'utils/pattern-match-util'
import { JobProgressionDto } from './dtos'

export interface JobProgressionStep {
  id: Id
  status: JobProgressionStepStatus
  response: JobProgressionStepStatus | null
  dateCreated?: Date
  dateResponded?: Date
}

export class JobProgression extends Model<JobProgressionDto, JobProgression> {
  @observable id = Id.none
  @observable jobId = Id.none
  @observable steps: JobProgressionStep[] = []
  @observable progressionFlow: JobProgressionFlow = JobProgressionFlow.NONE

  constructor() {
    super()
    makeObservable(this)
  }

  parse(progression: Partial<JobProgressionDto>): Partial<JobProgression> {
    return {
      id: progression?.id,
      jobId: progression.jobId,
      steps:
        progression?.steps?.map((s) => ({
          id: s.id,
          status: this.toStepStatus(s.progressionStep),
          response:
            s.response ===
            JobProgressionStepStatusProto.PROGRESSION_STEP_UNSPECIFIED
              ? null
              : this.toStepStatus(s.response),
          dateCreated: maybeParseDate(timestampToISO(s.dateCreated)),
          dateResponded: maybeParseDate(timestampToISO(s.dateResponded)),
        })) ?? [],

      progressionFlow: this.toProgressionFlow(
        progression.flow ?? JobProgressionFlowProto.NONE,
      ),
    }
  }

  toStepStatus(
    status: JobProgressionStepStatusProto,
  ): JobProgressionStepStatus {
    return status !== undefined
      ? match(status, {
          [JobProgressionStepStatusProto.OPEN_ITEMS]: () =>
            JobProgressionStepStatus.OPEN_ITEMS,
          [JobProgressionStepStatusProto.JOB_STARTED]: () =>
            JobProgressionStepStatus.JOB_STARTED,
          [JobProgressionStepStatusProto.DRAFT_IN_REVIEW]: () =>
            JobProgressionStepStatus.DRAFT_IN_REVIEW,
          [JobProgressionStepStatusProto.DRAFT_APPROVED]: () =>
            JobProgressionStepStatus.DRAFT_APPROVED,
          [JobProgressionStepStatusProto.DRAFT_REJECTED]: () =>
            JobProgressionStepStatus.DRAFT_REJECTED,
          [JobProgressionStepStatusProto.AUTHORIZATION_REQUESTED]: () =>
            JobProgressionStepStatus.AUTHORIZATION_REQUESTED,
          [JobProgressionStepStatusProto.AUTHORIZATION_SIGNED]: () =>
            JobProgressionStepStatus.AUTHORIZATION_SIGNED,
          [JobProgressionStepStatusProto.RETURN_FILED]: () =>
            JobProgressionStepStatus.RETURN_FILED,
          [JobProgressionStepStatusProto.NOT_STARTED]: () =>
            JobProgressionStepStatus.NOT_STARTED,
          [JobProgressionStepStatusProto.DELIVERABLE_COMPLETED]: () =>
            JobProgressionStepStatus.DELIVERABLE_COMPLETED,
          [JobProgressionStepStatusProto.PROGRESSION_STEP_UNSPECIFIED]: () =>
            JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED,
          [JobProgressionStepStatusProto.PREPARATION_COMPLETED]: () =>
            JobProgressionStepStatus.PREPARATION_COMPLETED,
          [JobProgressionStepStatusProto.REVIEW_COMPLETED]: () =>
            JobProgressionStepStatus.REVIEW_COMPLETED,
          [JobProgressionStepStatusProto.EXTENSION_IN_PROGRESS]: () =>
            JobProgressionStepStatus.EXTENSION_IN_PROGRESS,
          [JobProgressionStepStatusProto.EXTENSION_CONFIRMED]: () =>
            JobProgressionStepStatus.EXTENSION_CONFIRMED,
          [JobProgressionStepStatusProto.EXTENSION_ACCEPTED]: () =>
            JobProgressionStepStatus.EXTENSION_ACCEPTED,
          [JobProgressionStepStatusProto.EXTENSION_REJECTED]: () =>
            JobProgressionStepStatus.EXTENSION_REJECTED,
          [JobProgressionStepStatusProto.PROGRESSION_WAITING_ON_CLIENT]: () =>
            JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT,
          [JobProgressionStepStatusProto.READY_TO_FILE]: () =>
            JobProgressionStepStatus.READY_TO_FILE,
        })
      : JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED
  }

  toProgressionFlow(flow: JobProgressionFlowProto) {
    return match(flow, {
      [JobProgressionFlowProto.OUTSOURCING]: () =>
        JobProgressionFlow.OUTSOURCING,
      [JobProgressionFlowProto.RETAIL]: () => JobProgressionFlow.RETAIL,
      [JobProgressionFlowProto.ESSENTIAL]: () => JobProgressionFlow.ESSENTIAL,
      [JobProgressionFlowProto.NONE]: () => JobProgressionFlow.NONE,
      [JobProgressionFlowProto.PREPARATION]: () =>
        JobProgressionFlow.PREPARATION,
      [JobProgressionFlowProto.REVIEW]: () => JobProgressionFlow.REVIEW,
      [JobProgressionFlowProto.PERSONAL_EXTENSION]: () =>
        JobProgressionFlow.PERSONAL_EXTENSION,
      [JobProgressionFlowProto.BUSINESS_EXTENSION]: () =>
        JobProgressionFlow.BUSINESS_EXTENSION,
      [JobProgressionFlowProto.OUTSOURCING_EXTENSION]: () =>
        JobProgressionFlow.OUTSOURCING_EXTENSION,
    })
  }

  /**
   * Default value to use for prompts.
   */
  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() => new JobProgression())

export enum JobProgressionStepStatus {
  PROGRESSION_STEP_UNSPECIFIED,
  JOB_STARTED,
  OPEN_ITEMS,
  DRAFT_IN_REVIEW,
  DRAFT_REJECTED,
  DRAFT_APPROVED,
  AUTHORIZATION_REQUESTED,
  AUTHORIZATION_SIGNED,
  RETURN_FILED,
  NOT_STARTED,
  DELIVERABLE_COMPLETED,
  PREPARATION_COMPLETED,
  REVIEW_COMPLETED,
  EXTENSION_NOT_STARTED,
  EXTENSION_IN_PROGRESS,
  EXTENSION_CONFIRMED,
  EXTENSION_ACCEPTED,
  EXTENSION_REJECTED,
  PROGRESSION_WAITING_ON_CLIENT,
  READY_TO_FILE,
}

export enum JobProgressionFlow {
  NONE,
  RETAIL,
  OUTSOURCING,
  ESSENTIAL,
  PREPARATION,
  REVIEW,
  PERSONAL_EXTENSION,
  BUSINESS_EXTENSION,
  OUTSOURCING_EXTENSION,
}

export enum OverrideableJobProgressionStepStatus {
  RETURN_FILED,
  JOB_STARTED,
  OPEN_ITEMS,
}

export enum ExtensionProgressionFlow {
  PERSONAL,
  BUSINESS,
  OUTSOURCING,
}
